import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { PolarisClient, isMfaRequired } from "@digiits-agency/polaris-sdk";
import type { components, MfaRequired } from "@digiits-agency/polaris-sdk";
import { tokenStorage } from "./token-storage";
import { parseHashTokens, clearHashTokens } from "./hash-tokens";
import type { AuthConfig } from "./config";

// 2.1.47 renamed the session payload schema to `core-service_LoginResponse`
// and stopped aliasing it (the old `Login` alias is now the login *request*
// body, service-prefixed as `CoreServiceLogin`). Derive it from the exported
// schema map instead; the MFA challenge envelope is still aliased as
// `MfaRequired`. Re-export under the curated names the apps consume — no
// local DTO copies.
type Login = components["schemas"]["core-service_LoginResponse"];

/**
 * The signed-in user *as seen from the bound organization*.
 *
 * `roles` is another field the generated schema omits but the wire always
 * sends. It is per-membership — the same identity can be an Administrator here
 * and a viewer next door — so it must be re-read after every switch and never
 * cached across one.
 */
export type LoginUser = NonNullable<Login["user"]> & { roles?: string[] };
export type CapabilitiesMap = NonNullable<Login["capabilities"]>;
export type ModuleCapabilities = CapabilitiesMap[string];
export type ModuleAccessEntry = NonNullable<Login["moduleAccess"]>[number];

/**
 * One organization this identity belongs to, with the roles it holds *there*.
 *
 * Roles are per-membership: the same person can be an Administrator in one
 * organization and a viewer in another, so nothing derived from them may
 * outlive a switch.
 */
export type TenantMembership = NonNullable<Login["memberships"]>[number];

/**
 * The organization the session is bound to.
 *
 * Known gap: 2.1.78 dropped `tenant` from `core-service_LoginResponse` (2.1.75
 * still declared it), but `login` / `switchTenant` / `getMe` all still return
 * it on the wire. The membership summary is the same tenant shape, so derive
 * from it and add back only the three profile fields the session payload
 * carries beyond it. Delete the intersection once the schema declares `tenant`
 * again.
 */
export type LoginTenant = TenantMembership["tenant"] & {
  companyName?: string;
  currency?: string;
  timezone?: string;
};

/**
 * Session payload, with the two wire fields the generated type understates:
 * `tenant` (missing entirely) and `user` (declared optional because the schema
 * is shared with the MFA-challenge branch, which we narrow away before use).
 */
export type LoginResponse = Omit<Login, "user"> & {
  user: LoginUser;
  tenant?: LoginTenant;
};
export type CreatedOrganization = Awaited<
  ReturnType<PolarisClient["auth"]["createOrganization"]>
>;

export type MfaRequiredResponse = MfaRequired;

/**
 * Narrow a session-branch SDK response to what it actually carries on the wire.
 *
 * Every endpoint that mints a session (`login`, `switchTenant`,
 * `verifyMfaChallenge`, `getMe`) shares one generated schema with the MFA and
 * workspace-setup branches, so `user` reads as optional and `tenant` is missing
 * outright. Callers must have already ruled out those branches.
 */
function asSession(res: Login): LoginResponse {
  return res as LoginResponse;
}


// The login endpoint can also return a workspace-setup envelope that the SDK
// doesn't model (it carries a setup-scoped JWT, not a session). Keep it local.
export interface WorkspaceSetupRequiredResponse {
  nextStep: "provision_workspace";
  setupToken: string;
  expiresIn: number;
  user: LoginUser;
  message?: string;
}

export type AuthLoginResult =
  | LoginResponse
  | MfaRequiredResponse
  | WorkspaceSetupRequiredResponse;

export function isWorkspaceSetupRequired(
  result: AuthLoginResult,
): result is WorkspaceSetupRequiredResponse {
  return (
    typeof result === "object" &&
    result !== null &&
    "nextStep" in result &&
    (result as { nextStep?: string }).nextStep === "provision_workspace"
  );
}

interface AuthContextValue {
  user: LoginUser | null;
  tenant: LoginTenant | null;
  capabilities: CapabilitiesMap | null;
  moduleAccess: ModuleAccessEntry[] | null;
  /**
   * Organizations this identity can reach. Seeded from the login/switch
   * response, which populates it only when the user belongs to more than one —
   * so an empty array means "not loaded or nothing to switch to", never
   * "signed out". Call `refreshMemberships` for an authoritative list.
   */
  memberships: TenantMembership[];
  isAuthenticated: boolean;
  isLoading: boolean;
  authConfig: AuthConfig | null;
  login: (email: string, password: string) => Promise<AuthLoginResult>;
  verifyMfa: (
    mfaToken: string,
    code: string,
    options?: { trustDevice?: boolean },
  ) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Authoritative membership list for the current identity + product. */
  refreshMemberships: () => Promise<TenantMembership[]>;
  /**
   * Rebind the session to another organization. Mints a new token pair and
   * revokes the old one, so callers MUST drop every cache holding
   * organization-scoped data afterwards.
   */
  switchTenant: (tenantId: string) => Promise<LoginResponse>;
  /** Pin where the *next* sign-in lands. Does not move the current session. */
  setDefaultTenant: (tenantId: string) => Promise<TenantMembership[]>;
  /** Create a new organization. Does not move the current session. */
  createOrganization: (organization: string) => Promise<CreatedOrganization>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  client: PolarisClient;
  authConfig?: AuthConfig;
  onLogout?: () => void;
}

export function AuthProvider({
  children,
  client,
  authConfig,
  onLogout,
}: AuthProviderProps) {
  const [user, setUser] = useState<LoginUser | null>(null);
  const [tenant, setTenant] = useState<LoginTenant | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilitiesMap | null>(
    null,
  );
  const [moduleAccess, setModuleAccess] = useState<ModuleAccessEntry[] | null>(
    null,
  );
  const [memberships, setMemberships] = useState<TenantMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const handleLoginSuccess = useCallback((response: LoginResponse) => {
    if (response.accessToken) {
      tokenStorage.setToken(response.accessToken);
    }
    if (response.refreshToken) {
      tokenStorage.setRefreshToken(response.refreshToken);
    }
    setUser(response.user);
    setTenant(response.tenant ?? null);
    setCapabilities(response.capabilities ?? null);
    setModuleAccess(response.moduleAccess ?? null);
    // Absent for single-organization identities. Keep whatever we already had
    // rather than blanking a list a previous refresh filled in.
    if (response.memberships) {
      setMemberships(response.memberships);
    }
  }, []);

  const clearAuthState = useCallback(() => {
    tokenStorage.clearTokens();
    setUser(null);
    setTenant(null);
    setCapabilities(null);
    setModuleAccess(null);
    setMemberships([]);
  }, []);

  // On mount: check hash tokens first, then validate existing token via SDK
  useEffect(() => {
    const hashTokens = parseHashTokens();
    if (hashTokens) {
      tokenStorage.setToken(hashTokens.token);
      if (hashTokens.refresh) {
        tokenStorage.setRefreshToken(hashTokens.refresh);
      }
      clearHashTokens();
    }

    const token = tokenStorage.getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    client.auth
      .getMe()
      .then(asSession)
      .then((res) => {
        // Tenant-less session means provisioning never finished. Push the user
        // back through opero-web's login flow, which routes to /setup with a
        // fresh setupToken. Skip the redirect when loginUrl points at the
        // current origin — otherwise we'd loop on opero-web itself.
        if (res.tenant == null && authConfig?.loginUrl) {
          const sameOrigin =
            new URL(authConfig.loginUrl, window.location.href).origin ===
            window.location.origin;
          if (!sameOrigin) {
            tokenStorage.clearTokens();
            if (authConfig.redirectToLogin) {
              authConfig.redirectToLogin(authConfig.loginUrl);
            } else {
              window.location.href = authConfig.loginUrl;
            }
            return;
          }
        }
        setUser(res.user);
        setTenant(res.tenant ?? null);
        setCapabilities(res.capabilities ?? null);
        setModuleAccess(res.moduleAccess ?? null);
      })
      .catch(() => {
        clearAuthState();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [client, clearAuthState, authConfig?.loginUrl]);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthLoginResult> => {
      // The /v1/auth/identity/login endpoint can return either the session
      // payload or an MFA-required envelope; the SDK types only the session
      // branch, so we widen at the boundary. The generated request-body type
      // also mistakenly points at the `Login` response schema, so cast the
      // credentials to the SDK's declared body type.
      const result = (await client.auth.login({
        body: { email, password } as unknown as Parameters<
          typeof client.auth.login
        >[0]["body"],
      })) as AuthLoginResult;
      // The setup-required branch carries a workspace_setup-scoped JWT, not a
      // session token; handing it to handleLoginSuccess would corrupt session
      // state. Caller routes to /setup with the token in router state.
      if (!isMfaRequired(result) && !isWorkspaceSetupRequired(result)) {
        handleLoginSuccess(result);
      }
      return result;
    },
    [client, handleLoginSuccess],
  );

  const verifyMfa = useCallback(
    async (
      mfaToken: string,
      code: string,
      options?: { trustDevice?: boolean },
    ): Promise<LoginResponse> => {
      // The generated challenge request-body type is mis-referenced upstream,
      // so cast to the SDK's declared body type.
      const result = await client.auth.verifyMfaChallenge({
        body: (options?.trustDevice
          ? { mfaToken, code, trustDevice: true, trustDuration: "30days" }
          : { mfaToken, code }) as unknown as Parameters<
          typeof client.auth.verifyMfaChallenge
        >[0]["body"],
      });
      const session = asSession(result);
      handleLoginSuccess(session);
      return session;
    },
    [client, handleLoginSuccess],
  );

  const logout = useCallback(async () => {
    try {
      await client.auth.logout();
    } catch {
      // Ignore logout errors
    }
    clearAuthState();
    onLogout?.();
  }, [client, clearAuthState, onLogout]);

  const refreshProfile = useCallback(async () => {
    const res = asSession(await client.auth.getMe());
    setUser(res.user);
    setTenant(res.tenant ?? null);
    setCapabilities(res.capabilities ?? null);
    setModuleAccess(res.moduleAccess ?? null);
  }, [client]);

  const refreshMemberships = useCallback(async () => {
    const res = await client.auth.listMyTenants();
    const next = res.memberships ?? [];
    setMemberships(next);
    return next;
  }, [client]);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const result = asSession(
        await client.auth.switchTenant({ body: { tenantId } }),
      );
      // Same envelope as login: new tokens, new tenant, and — critically — the
      // roles and capabilities of the *target* organization.
      handleLoginSuccess(result);
      return result;
    },
    [client, handleLoginSuccess],
  );

  const setDefaultTenant = useCallback(
    async (tenantId: string) => {
      const res = await client.auth.setDefaultTenant({ body: { tenantId } });
      const next = res.memberships ?? [];
      setMemberships(next);
      return next;
    },
    [client],
  );

  const createOrganization = useCallback(
    async (organization: string) => {
      return client.auth.createOrganization({
        body: {
          organization,
          // Not optional in practice — see `AuthConfig.productSlug`.
          productSlug: authConfig?.productSlug,
        },
      });
    },
    [client, authConfig?.productSlug],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        capabilities,
        moduleAccess,
        memberships,
        isAuthenticated,
        isLoading,
        authConfig: authConfig ?? null,
        login,
        verifyMfa,
        logout,
        refreshProfile,
        refreshMemberships,
        switchTenant,
        setDefaultTenant,
        createOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };
